import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'

import { Spinner } from '../LoadingContainer/Spinner'
import {
  getReportProgressMessage,
  normalizeProgressPercent,
} from './reportProgressStages'

import './reportProgressIndicator.scss'

/**
 * Progress indicator for report generation.
 *
 * Shows a spinner, optional status message, and either:
 * - a determinate bar + percentage when `percent` is a valid 0–100 value, or
 * - an indeterminate animated bar when percent is unavailable.
 *
 * Never invents percentages; callers must pass real progress when available.
 */
export default function ReportProgressIndicator({
  active = true,
  message,
  stage,
  percent = null,
  title = 'Generating Report...',
  onCancel,
  cancelLabel = 'Cancel request',
  variant = 'modal',
  className = '',
  showCancel = Boolean(onCancel),
}) {
  if (!active) return null

  const statusMessage =
    message || getReportProgressMessage(stage, title) || title
  const normalizedPercent = normalizeProgressPercent(percent)
  const isDeterminate = normalizedPercent !== null
  const rootClass = [
    'report-progress-indicator',
    `report-progress-indicator--${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={rootClass}
      role="status"
      aria-busy="true"
      aria-live="polite"
      data-testid="report-progress-indicator"
    >
      <div className="report-progress-indicator__content">
        <span aria-hidden="true">
          <Spinner variant="primary" size="lg" />
        </span>
        <Stack direction="vertical" gap={1} className="align-items-center">
          <h4 className="report-progress-indicator__title mb-0">{title}</h4>
          <p
            className="report-progress-indicator__message mb-0"
            data-testid="report-progress-message"
          >
            {statusMessage}
          </p>
        </Stack>

        <div
          className="report-progress-indicator__track"
          role="progressbar"
          aria-label="Report generation progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={isDeterminate ? normalizedPercent : undefined}
          aria-valuetext={
            isDeterminate ? `${normalizedPercent}% complete` : statusMessage
          }
          data-testid="report-progress-bar"
          data-mode={isDeterminate ? 'determinate' : 'indeterminate'}
        >
          <div
            className={
              isDeterminate
                ? 'report-progress-indicator__fill report-progress-indicator__fill--determinate'
                : 'report-progress-indicator__fill report-progress-indicator__fill--indeterminate'
            }
            style={
              isDeterminate ? { width: `${normalizedPercent}%` } : undefined
            }
          />
        </div>

        {isDeterminate && (
          <p
            className="report-progress-indicator__percent mb-0"
            data-testid="report-progress-percent"
          >
            {normalizedPercent}%
          </p>
        )}

        {showCancel && typeof onCancel === 'function' && (
          <Button
            size="lg"
            variant="outline-light"
            onClick={onCancel}
            className="report-progress-indicator__cancel"
            data-testid="report-progress-cancel"
          >
            {cancelLabel}
          </Button>
        )}
      </div>
    </div>
  )
}
